import type { ComponentProps, ComponentPropsWithRef } from "react";

import { FileIcon } from "@untitledui/file-icons";
import { SearchLg } from "@untitledui/icons";
import { Children, createContext, isValidElement, useContext } from "react";

import type { BackgroundPatternProps } from "@/assets/background-patterns";

import { BackgroundPattern } from "@/assets/background-patterns";
import { FeaturedIcon as FeaturedIconbase } from "@/assets/icons/featured-icon/featured-icon";
import { Illustration as Illustrations } from "@/assets/illustrations";
import { cx } from "@/utils/cx";

interface RootContextProps {
	size?: "lg" | "md" | "sm";
}

const RootContext = createContext<RootContextProps>({ size: "lg" });

interface RootProps extends ComponentPropsWithRef<"div">, RootContextProps {}

const Root = ({ size = "lg", ...props }: RootProps) => {
	return (
		<RootContext.Provider value={{ size }}>
			<div
				{...props}
				className={cx(
					"mx-auto flex w-full max-w-lg flex-col items-center justify-center",
					props.className,
				)}
			/>
		</RootContext.Provider>
	);
};

const FeaturedIcon = ({
	color = "gray",
	icon = SearchLg,
	size: sizeProp,
	theme = "modern",
	...props
}: ComponentPropsWithRef<typeof FeaturedIconbase>) => {
	const { size: rootSize } = useContext(RootContext);

	const size =
		sizeProp ?? (rootSize === "sm" ? "sm" : rootSize === "md" ? "md" : "xl");

	return (
		<FeaturedIconbase {...props} {...{ color, icon, theme }} size={size} />
	);
};

const Illustration = ({
	color = "gray",
	size = "lg",
	type = "cloud",
	...props
}: ComponentPropsWithRef<typeof Illustrations>) => {
	const { size: rootSize } = useContext(RootContext);

	return (
		<Illustrations
			role="img"
			{...props}
			{...{ color, type }}
			className={cx("z-10", props.className)}
			size={rootSize === "sm" ? "sm" : rootSize === "md" ? "md" : size}
		/>
	);
};

interface FileTypeIconProps extends ComponentPropsWithRef<"div"> {
	theme?: ComponentProps<typeof FileIcon>["variant"];
	type?: ComponentProps<typeof FileIcon>["type"];
}

const FileTypeIcon = ({
	theme = "solid",
	type = "folder",
	...props
}: FileTypeIconProps) => {
	return (
		<div
			{...props}
			className={cx(
				"relative z-10 flex rounded-full bg-linear-to-b from-gray-50 to-gray-200 p-8",
				props.className,
			)}
		>
			<FileIcon
				className="size-10 drop-shadow-sm"
				type={type}
				variant={theme}
			/>
		</div>
	);
};

interface HeaderProps extends ComponentPropsWithRef<"div"> {
	pattern?: "none" | BackgroundPatternProps["pattern"];
	patternSize?: "lg" | "md" | "sm";
}

const Header = ({
	pattern = "circle",
	patternSize = "md",
	...props
}: HeaderProps) => {
	const { size } = useContext(RootContext);
	// Whether we are passing `Illustration` component as children.
	const hasIllustration = Children.toArray(props.children).some(
		(headerChild) =>
			isValidElement(headerChild) && headerChild.type === Illustration,
	);

	return (
		<header
			{...props}
			className={cx(
				"relative mb-4",
				(size === "md" || size === "lg") && "mb-5",
				hasIllustration && size === "lg" && "mb-6!",
				props.className,
			)}
		>
			{pattern !== "none" && (
				<BackgroundPattern
					className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
					pattern={pattern}
					size={patternSize}
				/>
			)}
			{props.children}
		</header>
	);
};

const Content = (props: ComponentPropsWithRef<"div">) => {
	const { size } = useContext(RootContext);

	return (
		<main
			{...props}
			className={cx(
				"z-10 mb-6 flex w-full flex-col items-center justify-center gap-1",
				(size === "md" || size === "lg") && "mb-8 gap-2",
				props.className,
			)}
		/>
	);
};

const Footer = (props: ComponentPropsWithRef<"div">) => {
	return (
		<footer {...props} className={cx("z-10 flex gap-3", props.className)} />
	);
};

const Title = (props: ComponentPropsWithRef<"h1">) => {
	const { size } = useContext(RootContext);

	return (
		<h1
			{...props}
			className={cx(
				"text-md font-semibold text-primary",
				size === "md" && "text-lg font-semibold",
				size === "lg" && "text-xl font-semibold",
				props.className,
			)}
		/>
	);
};

const Description = (props: ComponentPropsWithRef<"p">) => {
	const { size } = useContext(RootContext);

	return (
		<p
			{...props}
			className={cx(
				"text-center text-sm text-tertiary",
				size === "lg" && "text-md",
				props.className,
			)}
		/>
	);
};

const EmptyState = Root as typeof Root & {
	Content: typeof Content;
	Description: typeof Description;
	FeaturedIcon: typeof FeaturedIcon;
	FileTypeIcon: typeof FileTypeIcon;
	Footer: typeof Footer;
	Header: typeof Header;
	Illustration: typeof Illustration;
	Title: typeof Title;
};

EmptyState.Title = Title;
EmptyState.Header = Header;
EmptyState.Footer = Footer;
EmptyState.Content = Content;
EmptyState.Description = Description;
EmptyState.Illustration = Illustration;
EmptyState.FeaturedIcon = FeaturedIcon;
EmptyState.FileTypeIcon = FileTypeIcon;

export { EmptyState };
